import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Fab,
  Chip,
  Stack,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import AddIcon from '@mui/icons-material/Add';
import PrescriptionModalRouter from '../prescriptionModal/prescriptionModalRouter';
import Loading from '../../../../components/loading';
import SuccessMessage from '../../../../components/successMessage';
import ErrorMessage from '../../../../components/errorMessage';
import { usePrescriptions } from '../hooks/usePrescriptions';
import SignHealthDocumentModal from '../../../../components/signHealthDocumentModal/signHealthDocumentModal';
import { useSignedDocumentActions } from '../../../../hooks/useSignedDocumentActions';
import { useSignPrescription } from '../hooks/useSignPrescription';
import { useSignedPrescriptionDocument } from '../hooks/useSignedPrescriptionDocument';
import { formatPrescriptionSummary, hasControlledMedicines } from '../prescriptionSignature';

export default function PrescriptionListMobile() {
  const { patientId } = useParams();

  const {
    prescriptions,
    isLoading,
    error: prescriptionsError,
    refetch: refetchPrescriptions,
  } = usePrescriptions(patientId);

  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isModalOpen, setModalOpen] = useState(false);

  const { sign: signPrescription } = useSignPrescription();
  const { fetchUrl: fetchSignedPrescriptionUrl } = useSignedPrescriptionDocument();
  const {
    documentToSign: prescriptionToSign,
    openSignModal,
    closeSignModal,
    onSigned,
    openSignedDocument,
    isOpeningSignedDocument,
  } = useSignedDocumentActions({
    fetchSignedDocumentUrl: (prescriptionId) =>
      fetchSignedPrescriptionUrl(patientId, prescriptionId),
    refetch: refetchPrescriptions,
    onSuccess: (message) => {
      setShowSuccessMessage(true);
      setSuccessMessage(message);
    },
    onError: (message) => {
      setShowErrorMessage(true);
      setErrorMessage(message);
    },
  });

  useEffect(() => {
    if (prescriptionsError) {
      setErrorMessage(prescriptionsError);
      setShowErrorMessage(true);
    }
  }, [prescriptionsError]);

  const onSavePrescription = (returnFromApi) => {
    if (returnFromApi.data) {
      setShowSuccessMessage(true);
      setSuccessMessage(returnFromApi.data);
    } else {
      setShowErrorMessage(true);
      setErrorMessage(returnFromApi.response?.data);
    }
    setModalOpen(false);
    refetchPrescriptions();
  };

  const printPrescription = (prescriptionId) => {
    window.open(`/prescription-to-print/${patientId}/${prescriptionId}`, '_blank');
  };

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Loading isToHidden={isLoading === false && isOpeningSignedDocument === false} />
        {showSuccessMessage && (
          <SuccessMessage
            message={successMessage}
            show={showSuccessMessage}
            setShow={setShowSuccessMessage}
          />
        )}
        {showErrorMessage && (
          <ErrorMessage
            message={errorMessage}
            show={showErrorMessage}
            setShow={setShowErrorMessage}
          />
        )}

        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
          Receituários
        </Typography>

        {prescriptions.length === 0 ? (
          <Typography align="center" sx={{ py: 4 }}>
            Nenhuma receita cadastrada ainda.
          </Typography>
        ) : (
          <List disablePadding>
            {prescriptions.map((p) => (
              <ListItem
                key={p.receituarioId}
                divider
                sx={{ pr: 12 }}
                secondaryAction={
                  <Stack direction="row">
                    {p.signed && (
                      <IconButton
                        color="primary"
                        aria-label="abrir PDF assinado"
                        onClick={() => openSignedDocument(p.receituarioId)}
                      >
                        <PictureAsPdfOutlinedIcon />
                      </IconButton>
                    )}
                    {!p.signed && !hasControlledMedicines(p) && (
                      <IconButton
                        color="primary"
                        aria-label="assinar digitalmente"
                        onClick={() => openSignModal(p)}
                      >
                        <DrawOutlinedIcon />
                      </IconButton>
                    )}
                    <IconButton color="primary" onClick={() => printPrescription(p.receituarioId)}>
                      <PrintIcon />
                    </IconButton>
                  </Stack>
                }
              >
                <ListItemText
                  primary={`Data: ${p.data}`}
                  primaryTypographyProps={{ fontWeight: 'bold' }}
                  secondaryTypographyProps={{ component: 'div' }}
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {p.nomeDoDentista}
                      </Typography>
                      {p.signed && (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          icon={<VerifiedUserOutlinedIcon />}
                          label={`Assinado em ${p.signedAt}`}
                          sx={{ mt: 0.5 }}
                        />
                      )}
                      {!p.signed && hasControlledMedicines(p) && (
                        <Typography variant="caption" color="warning.main" display="block">
                          Controlado: assinatura digital indisponível, imprima.
                        </Typography>
                      )}
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      <Fab
        color="primary"
        aria-label="adicionar receituário"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
        }}
        onClick={() => setModalOpen(true)}
      >
        <AddIcon />
      </Fab>

      <SignHealthDocumentModal
        open={!!prescriptionToSign}
        onClose={closeSignModal}
        onSigned={onSigned}
        onSign={(certificatePassword) =>
          signPrescription(patientId, prescriptionToSign.receituarioId, certificatePassword)
        }
        title="Assinar receituário digitalmente"
        summary={formatPrescriptionSummary(prescriptionToSign)}
      />

      <PrescriptionModalRouter
        open={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSave={onSavePrescription}
      />
    </>
  );
}
